import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { useT } from "@/hooks/useT";
import { CheckCircle2, Circle, Trash2, Plus, CalendarDays } from "lucide-react";

export default function Planner() {
  useT(); // subscribe to global language changes so header/children re-render
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [credits, setCredits] = useState(0);
  const [newTitle, setNewTitle] = useState("");

  const load = () => api.get("/planner").then(({ data }) => setItems(data || []));

  useEffect(() => {
    load();
    api.get("/credits/balance").then(({ data }) => setCredits(data.credits)).catch(() => {});
  }, []);

  const toggle = async (it) => {
    try {
      await api.patch(`/planner/${it.item_id}`, { completed: !it.completed });
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/planner/${id}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };
  const create = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await api.post("/planner", { title: newTitle.trim(), due_at: selected.toISOString(), source_type: "custom" });
      setNewTitle("");
      load();
      toast.success("Added");
    } catch {
      toast.error("Add failed");
    }
  };

  const sameDay = (a, b) => {
    const x = new Date(a);
    return x.getFullYear() === b.getFullYear() && x.getMonth() === b.getMonth() && x.getDate() === b.getDate();
  };
  const dueOnSelected = items.filter((i) => i.due_at && sameDay(i.due_at, selected));
  const upcoming = items
    .filter((i) => !i.completed)
    .sort((a, b) => new Date(a.due_at || 0) - new Date(b.due_at || 0))
    .slice(0, 12);

  const dotDays = items
    .filter((i) => i.due_at && !i.completed)
    .map((i) => {
      const d = new Date(i.due_at);
      d.setHours(0, 0, 0, 0);
      return d;
    });

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="planner-page">
      <Header credits={credits} onOpenAd={() => {}} onOpenCheckout={() => {}} />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <CalendarDays size={12} className="text-[#00FF9D]" /> OAK Planner
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Your learning calendar</h1>
          <p className="text-white/55 text-sm max-w-xl mt-1">
            Every path you generate can auto-populate a study schedule. Mark steps done, add custom tasks, never drift.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
          <div className="md:col-span-2 glass p-5" data-testid="planner-calendar">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => d && setSelected(d)}
              modifiers={{ due: dotDays }}
              modifiersStyles={{
                due: { color: "#00FF9D", fontWeight: 700, textShadow: "0 0 8px rgba(0,255,157,0.5)" },
              }}
              className="rounded-md"
            />
            <form onSubmit={create} className="mt-4 flex gap-2">
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={`Task on ${selected.toLocaleDateString()}`}
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0044FF]/60"
                data-testid="planner-new-title"
              />
              <button className="btn-primary rounded-lg px-3 text-sm" data-testid="planner-add-btn">
                <Plus size={14} />
              </button>
            </form>
          </div>

          <div className="md:col-span-3 grid grid-cols-1 gap-5">
            <TaskList
              title={`On ${selected.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}`}
              items={dueOnSelected}
              onToggle={toggle}
              onRemove={remove}
              testid="planner-day-tasks"
              emptyText="No tasks on this day — tap the date to plan."
            />
            <TaskList
              title="Upcoming"
              items={upcoming}
              onToggle={toggle}
              onRemove={remove}
              testid="planner-upcoming"
              emptyText="No upcoming tasks. Sync a path from the Academy!"
            />
          </div>
        </div>
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}

function TaskList({ title, items, onToggle, onRemove, testid, emptyText }) {
  return (
    <section className="glass p-5" data-testid={testid}>
      <div className="label-eyebrow mb-3">{title}</div>
      {items.length === 0 ? (
        <div className="text-white/40 text-sm">{emptyText}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.item_id} className="flex items-center gap-3 border-b border-white/5 last:border-0 pb-2">
              <button onClick={() => onToggle(it)} className="shrink-0" data-testid="planner-toggle">
                {it.completed ? (
                  <CheckCircle2 size={18} className="text-[#00FF9D]" />
                ) : (
                  <Circle size={18} className="text-white/30 hover:text-white/70" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${it.completed ? "line-through text-white/40" : "text-white/85"} truncate`}>
                  {it.title}
                </div>
                {it.due_at && (
                  <div className="text-[11px] text-white/40 font-mono-oak">
                    {new Date(it.due_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
              <button onClick={() => onRemove(it.item_id)} className="text-white/30 hover:text-rose-400 p-1" title="Remove">
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
